import { useState, useEffect } from "react";
import { getRoles, getRoleMenus, toggleRoleMenuAccess, createRole } from "../roles/api/roles";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import * as HeroIcons from '@heroicons/react/24/outline';
import { Search, Plus, Settings } from "lucide-react";

interface Role {
    role_id: number;
    role_desc: string;
}

interface Menu {
    menu_key: number;
    menu_desc: string;
    icon_name: string;
    has_access: boolean;
    display_order: number;
    submenu?: Menu[];
}

const Roles = () => {
    const [roles, setRoles] = useState<Role[]>([]);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [roleMenus, setRoleMenus] = useState<Menu[]>([]);
    const [loading, setLoading] = useState(true);
    const [menusLoading, setMenusLoading] = useState(false);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    // Create role modal state
    const [createRoleModalOpen, setCreateRoleModalOpen] = useState(false);
    const [createRoleLoading, setCreateRoleLoading] = useState(false);
    const [createRoleError, setCreateRoleError] = useState("");
    const [roleName, setRoleName] = useState("");

    // Fetch roles on component mount
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                setLoading(true);
                setError("");
                const response = await getRoles();
                setRoles(response || []);
            } catch (err: any) {
                console.error("Error fetching roles:", err);
                setError(err?.message || "Failed to fetch roles");
            } finally {
                setLoading(false);
            }
        };

        fetchRoles();
    }, []);

    // Fetch role menus when a role is selected
    useEffect(() => {
        const fetchRoleMenus = async () => {
            if (!selectedRole) {
                setRoleMenus([]);
                return;
            }

            try {
                setMenusLoading(true);
                const response = await getRoleMenus(selectedRole.role_id.toString());
                const menus = response || [];
                setRoleMenus(menus);
            } catch (err) {
                console.error("Error fetching role menus:", err);
                setRoleMenus([]);
            } finally {
                setMenusLoading(false);
            }
        };

        fetchRoleMenus();
    }, [selectedRole]);

    const filteredRoles = roles.filter(role =>
        role.role_desc.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleRoleSelect = (role: Role) => {
        setSelectedRole(role);
    };

    const handleToggleAccess = async (menuId: number) => {
        if (!selectedRole) return;

        try {
            await toggleRoleMenuAccess(selectedRole.role_id.toString(), menuId.toString());
            // Refresh the menus to get updated access status
            const response = await getRoleMenus(selectedRole.role_id.toString());
            setRoleMenus(response || []);
        } catch (err) {
            console.error("Error toggling menu access:", err);
        }
    };

    const handleOpenCreateRoleModal = () => {
        setCreateRoleModalOpen(true);
        setCreateRoleError("");
        setRoleName("");
    };

    const handleCloseCreateRoleModal = () => {
        setCreateRoleModalOpen(false);
        setCreateRoleError("");
        setRoleName("");
    };

    const handleCreateRole = async () => {
        try {
            setCreateRoleLoading(true);
            setCreateRoleError("");

            // Validation
            if (!roleName.trim()) {
                setCreateRoleError("Role name is required");
                return;
            }

            const roleData = {
                role_desc: roleName.trim()
            };

            await createRole(roleData);

            // Refresh roles list
            const response = await getRoles();
            setRoles(response || []);

            handleCloseCreateRoleModal();
        } catch (err: any) {
            console.error("Error creating role:", err);

            // Handle validation errors from API
            if (err?.response?.status === 422 && err?.response?.data?.detail) {
                const details = err.response.data.detail;
                if (Array.isArray(details) && details.length > 0) {
                    const firstError = details[0];
                    setCreateRoleError(firstError.msg || "Validation error");
                } else {
                    setCreateRoleError("Validation error occurred");
                }
            } else {
                setCreateRoleError(err?.message || "Failed to create role");
            }
        } finally {
            setCreateRoleLoading(false);
        }
    };

    const renderMenuItem = (menu: Menu, isSubmenu = false) => {
        const IconComponent = (HeroIcons as any)[menu.icon_name] || HeroIcons.HomeIcon;
        const EyeIcon = menu.has_access ? HeroIcons.EyeIcon : HeroIcons.EyeSlashIcon;
        const menuLabel = menu.menu_desc.charAt(0).toUpperCase() + menu.menu_desc.slice(1);

        return (
            <div
                key={menu.menu_key}
                className={`flex items-center justify-between p-3 rounded-lg border transition-all ${isSubmenu ? 'ml-6 bg-gray-50' : 'bg-white'
                    } border-gray-200 hover:border-gray-300`}
            >
                <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2.5">
                        <IconComponent className="w-4 h-4 text-gray-500" />
                        <span className="font-medium text-sm text-gray-700">
                            {menuLabel}
                        </span>
                    </div>
                </div>

                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => handleToggleAccess(menu.menu_key)}
                        className={`p-1 rounded hover:bg-gray-100 transition-colors ${menu.has_access ? 'text-green-600' : 'text-red-500'
                            }`}
                        title={menu.has_access ? 'Remove access' : 'Grant access'}
                    >
                        <EyeIcon className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="flex h-[calc(100vh-200px)] gap-6">
            {/* Left Pane - Roles List */}
            <div className="w-1/3 flex flex-col">
                <Card className="flex-1">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-base font-medium">Roles</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Search Box */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <Input
                                placeholder="Search roles..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>

                        {/* Create Role Button */}
                        <Button className="w-full" variant="outline" onClick={handleOpenCreateRoleModal}>
                            <Plus className="w-4 h-4 mr-2" />
                            Create Role
                        </Button>

                        {/* Roles List */}
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                            {loading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-500"></div>
                                </div>
                            ) : error ? (
                                <Alert variant="destructive">
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            ) : filteredRoles.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    No roles found
                                </div>
                            ) : (
                                filteredRoles.map((role) => (
                                    <div
                                        key={role.role_id}
                                        onClick={() => handleRoleSelect(role)}
                                        className={`p-2 rounded-lg border cursor-pointer transition-all ${selectedRole?.role_id === role.role_id
                                                ? 'border-blue-200 bg-blue-50 text-blue-900'
                                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <Settings className="w-4 h-4" />
                                            <span className="font-medium text-sm capitalize">
                                                {role.role_desc}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Right Pane - Role Permissions */}
            <div className="flex-1 flex flex-col">
                <Card className="flex-1 flex flex-col">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-base font-medium">
                            {selectedRole ? (
                                <>Role Permissions - <span className="capitalize">{selectedRole.role_desc}</span></>
                            ) : (
                                'Select a Role'
                            )}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 overflow-hidden">
                        {!selectedRole ? (
                            <div className="flex items-center justify-center h-64 text-gray-500">
                                <div className="text-center">
                                    <Settings className="w-8 h-8 mx-auto mb-4 text-gray-300" />
                                    <p>Select a role from the left to view and edit permissions</p>
                                </div>
                            </div>
                        ) : menusLoading ? (
                            <div className="flex items-center justify-center h-64">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
                            </div>
                        ) : (
                            <div className="space-y-2 h-full overflow-y-auto">
                                {roleMenus.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        No menus available for this role
                                    </div>
                                ) : (
                                    roleMenus
                                        .sort((a, b) => a.display_order - b.display_order)
                                        .map((menu) => (
                                            <div key={menu.menu_key} className="space-y-2">
                                                {renderMenuItem(menu)}
                                                {menu.submenu && menu.submenu.length > 0 && (
                                                    <div className="space-y-2">
                                                        {menu.submenu
                                                            .sort((a, b) => a.display_order - b.display_order)
                                                            .map((submenu) => renderMenuItem(submenu, true))}
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Create Role Modal */}
            <Dialog open={createRoleModalOpen} onOpenChange={setCreateRoleModalOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-base font-medium">Create New Role</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {createRoleError && (
                            <Alert variant="destructive">
                                <AlertDescription>{createRoleError}</AlertDescription>
                            </Alert>
                        )}

                        {/* Role Name */}
                        <div className="space-y-2">
                            <Label htmlFor="roleName" className="text-sm font-medium">Role Name *</Label>
                            <Input
                                id="roleName"
                                value={roleName}
                                onChange={(e) => setRoleName(e.target.value)}
                                placeholder="Enter role name"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !createRoleLoading) {
                                        handleCreateRole();
                                    }
                                }}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={handleCloseCreateRoleModal}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreateRole} disabled={createRoleLoading}>
                            {createRoleLoading ? 'Creating...' : 'Create Role'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Roles;
